import type { ReactNode } from 'react';

import { TextInput } from 'cpool-components/input/TextInput';
import { MAIL_CONTENT_KINDS, type TEMPLATE_TYPE } from 'cpool-constants';
import { Eta } from 'eta/core';
import type { FieldArrayWithId, UseFormRegister } from 'react-hook-form';

import { reactStringReplace } from '../reactStringReplace';
import type { InputKind } from '~/features/headerFooterTemplate/types';

type RenderEtaMailTemplateProps = {
    contentKind: InputKind;
    etaContext: Record<string, string | (() => string)>;
    isOfferTemplate: boolean;
    template: string;
    templateType: TEMPLATE_TYPE;
};

type RenderEtaMailTemplateWithFreetextFieldsProps = RenderEtaMailTemplateProps & {
    freeTextPlaceholderText: string;
    offerFields: FieldArrayWithId<
        {
            freetext: {
                offers: {
                    value: string;
                }[];
            };
        },
        'freetext.offers',
        'id'
    >[];
    fields: FieldArrayWithId<
        {
            freetext: {
                body: {
                    value: string;
                }[];
            };
        },
        'freetext.body',
        'id'
    >[];
    register: UseFormRegister<{
        freetext: {
            body: {
                value: string;
            }[];
            offers: {
                value: string;
            }[];
        };
    }>;
};

export const FREETEXT_BODY_PLACEHOLDER = '##FREITEXT##';
export const FREETEXT_OFFER_PLACEHOLDER = '##FREITEXT_OFFER##';

export const renderPreviewEtaMailTemplate = ({
    contentKind,
    etaContext,
    isOfferTemplate,
    template,
    templateType,
}: RenderEtaMailTemplateProps) => {
    // NOTE: without "autoTrim = false" we would remove line breaks in the template and without "autoEscape = false" we would escape the input field in the freetext value
    const eta = new Eta({ autoEscape: false, autoTrim: false });

    let renderedText: string | ReactNode[];

    renderedText = eta.renderString(template, etaContext);

    const numFreeTextFields = {
        body: (renderedText.match(new RegExp(FREETEXT_BODY_PLACEHOLDER, 'gm')) ?? []).length,
        offers: (renderedText.match(new RegExp(FREETEXT_OFFER_PLACEHOLDER, 'gm')) ?? []).length,
    };

    // NOTE: this is to ensure the preview is rendered correctly in the browser as html
    // also for offer templates we don't want the line breaks to be removed
    // since it will be rendered again in the body of the email and the eta.renderString would stringify the br tags
    if (contentKind === MAIL_CONTENT_KINDS.MAIL_CONTENT && !isOfferTemplate) {
        let brIndex = 0;
        renderedText = reactStringReplace(renderedText, /(\r\n|\n|\r)/gm, () => (
            <br key={`br-${contentKind}-${templateType}-${brIndex++}`} />
        ));
    }

    return { numFreeTextFields, renderedText };
};

export const renderPreviewEtaMailTemplateWithFreetextFields = ({
    contentKind,
    etaContext,
    fields,
    freeTextPlaceholderText,
    isOfferTemplate,
    offerFields,
    register,
    template,
    templateType,
}: RenderEtaMailTemplateWithFreetextFieldsProps) => {
    const { numFreeTextFields, renderedText } = renderPreviewEtaMailTemplate({
        contentKind,
        etaContext,
        isOfferTemplate,
        template,
        templateType,
    });

    if (numFreeTextFields.body === 0 && numFreeTextFields.offers === 0) return renderedText;

    let freeTextFieldsIndexBody = -1;
    let freeTextFieldsIndexOffers = -1;
    // NOTE: This regex needs to be one capturing group to ensure that the match is not split into multiple groups which would break the reactStringReplace function
    const regex = new RegExp(`(${FREETEXT_BODY_PLACEHOLDER}|${FREETEXT_OFFER_PLACEHOLDER})`, 'gm');
    const replacedText = reactStringReplace(renderedText, regex, (match) => {
        if (match === FREETEXT_BODY_PLACEHOLDER) {
            freeTextFieldsIndexBody++;
            if (freeTextFieldsIndexBody >= numFreeTextFields.body) {
                return '';
            }
            // NOTE: this is currently not needed since the freetext fields are already replaced like this by the eta renderer
            if (contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT) {
                return `<input type="text" key="${
                    fields[freeTextFieldsIndexBody]?.id ?? freeTextFieldsIndexBody
                }" class="border border-light-2 rounded-md py-2 my-2 text-sm text-dark-1 w-full" placeholder="${freeTextPlaceholderText}" name="freetext.body.${freeTextFieldsIndexBody}.value" />`;
            }
            const fieldName = `freetext.body.${freeTextFieldsIndexBody}.value` as const;
            return (
                <TextInput
                    id={fieldName}
                    className="my-2 w-full"
                    placeholder={freeTextPlaceholderText}
                    key={fields[freeTextFieldsIndexBody]?.id ?? freeTextFieldsIndexBody}
                    {...register(fieldName, { required: false })}
                />
            );
        }
        if (match === FREETEXT_OFFER_PLACEHOLDER) {
            freeTextFieldsIndexOffers++;
            if (freeTextFieldsIndexOffers >= numFreeTextFields.offers) {
                return '';
            }
            if (contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT) {
                return `<input type="text" key="${
                    offerFields[freeTextFieldsIndexOffers]?.id ?? freeTextFieldsIndexOffers
                }" class="border border-light-2 rounded-md py-2 my-2 text-sm text-dark-1 w-full" placeholder="${freeTextPlaceholderText}" name="freetext.offers.${freeTextFieldsIndexOffers}.value" />`;
            }

            const fieldName = `freetext.offers.${freeTextFieldsIndexOffers}.value` as const;
            return (
                <TextInput
                    id={fieldName}
                    className="my-2 w-full"
                    placeholder={freeTextPlaceholderText}
                    key={offerFields[freeTextFieldsIndexOffers]?.id ?? freeTextFieldsIndexOffers}
                    {...register(fieldName, { required: false })}
                />
            );
        }
        return '';
    });

    return contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT ? replacedText.join('\n') : replacedText;
};
